import {
  ContentPageTheme,
  PublicReviewPage,
  PublicReviewPageReview,
  ReviewPageConfigSettings,
  ReviewPageListing,
  isCustomCTAType,
} from "@/services/bedrock";
import { renderLiquid } from "@/utils/liquidHelpers";
import {
  DUMMY_REVIEWS,
  DummyReview,
  SAMPLE_THEME,
  buildSampleSummary,
  findTheme,
} from "../../components/sampleReviews";

const SAMPLE_PLAYER = {
  guid: "sample-player",
  fullName: "Alex Smith",
  title: "Loan Officer",
  photoUrl: null,
  companyName: "Acme Mortgage",
  complianceDisclaimer: "NMLS #123456",
};

const SAMPLE_LIQUID_VARIABLES = {
  player: {
    first_name: "Alex",
    last_name: "Smith",
    full_name: "Alex Smith",
    title: "Loan Officer",
    company_name: "Acme Mortgage",
    compliance_disclaimer: "NMLS #123456",
    industry_role: "Loan Officer",
    licensed_states: "AZ, UT",
  },
};

const SAMPLE_LISTING: ReviewPageListing = {
  name: "Acme Mortgage",
  addressLines: ["260 S 2500 W", "Suite 203"],
  city: "Pleasant Grove",
  state: "UT",
  postalCode: "84062",
  formattedAddress: "260 S 2500 W Suite 203, Pleasant Grove, UT 84062",
  latitude: 40.36,
  longitude: -111.77,
  timeZone: "America/Denver",
  hours: ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY"].map(day => ({
    openDay: day as "MONDAY",
    openTime: "08:00",
    closeDay: day as "MONDAY",
    closeTime: "18:00",
  })),
  openStatus: "OPEN",
  phone: "(801) 555-0123",
};

const EXTRA_REVIEWS: DummyReview[] = [
  {
    displayName: "Chris P.",
    approvedForSocial: true,
    content:
      "The process took longer than we hoped, but the team kept us informed at every step and we closed on time.",
    sourceSystem: "GL",
    ratingType: "star",
    starRating: 3,
    sentiment: "neutral",
    publishedAt: "2025-07-12T00:00:00Z",
  },
  {
    displayName: "Dana W.",
    approvedForSocial: true,
    content: "Communication was slow during underwriting and we had to follow up several times to get answers.",
    sourceSystem: "FB",
    ratingType: "star",
    starRating: 2,
    sentiment: "negative",
    publishedAt: "2025-06-28T00:00:00Z",
  },
];

const SAMPLE_REPLIES: Record<string, string> = {
  "Ashley R.": "Thank you, Ashley! It was a pleasure helping you and your husband get into your new home.",
  "Dana W.":
    "Thanks for the feedback, Dana. We've changed how we share underwriting updates so this doesn't happen again.",
};

const sampleReviews = (settings: ReviewPageConfigSettings): PublicReviewPageReview[] => {
  const reviews = [...DUMMY_REVIEWS, ...EXTRA_REVIEWS]
    .filter(review => !settings.approvedForSocialOnly || review.approvedForSocial)
    .map(({ approvedForSocial: _approvedForSocial, ...review }, index) => {
      const reply = SAMPLE_REPLIES[review.displayName ?? ""];
      return {
        ...review,
        guid: `sample-review-${index}`,
        sentiment: review.sentiment ?? "positive",
        reply: reply ? { content: reply, displayName: SAMPLE_PLAYER.fullName } : null,
      };
    });

  if (settings.reviewSortType === "featured") return reviews;
  return reviews.sort((a, b) => (b.publishedAt ?? "").localeCompare(a.publishedAt ?? ""));
};

const sampleCallToAction = ({ callToAction }: ReviewPageConfigSettings) =>
  callToAction
    ? {
        label: callToAction.label,
        url: (isCustomCTAType(callToAction.type) && callToAction.customLink) || `#${callToAction.type}`,
      }
    : null;

export const buildReviewPagePreviewData = (
  settings: ReviewPageConfigSettings,
  themes: ContentPageTheme[]
): PublicReviewPage => {
  const theme = findTheme(themes, settings.contentPageThemeId) ?? SAMPLE_THEME;
  const reviews = sampleReviews(settings);
  const summary = buildSampleSummary(settings);

  return {
    url: "#sample-reviews-page",
    headline: renderLiquid(settings.headline, SAMPLE_LIQUID_VARIABLES),
    subtitle: renderLiquid(settings.subtitle, SAMPLE_LIQUID_VARIABLES),
    settings: {
      useCondensedQuotes: settings.useCondensedQuotes,
      showReviewReplies: settings.showReviewReplies,
      showSourceFilter: settings.showSourceFilter,
    },
    player: SAMPLE_PLAYER,
    theme,
    summary,
    listing: settings.showOfficeDetails ? SAMPLE_LISTING : null,
    links: {
      websiteUrl: null,
      formUrl: "#write-a-review",
      callToAction: sampleCallToAction(settings),
    },
    socialLinks: settings.showSocialLinks
      ? [
          { socialNetworkId: "FB", url: "#facebook" },
          { socialNetworkId: "LI", url: "#linkedin" },
          { socialNetworkId: "IG", url: "#instagram" },
        ]
      : [],
    filters: {
      sentiment: "positive",
      sourceSystems: [],
      availableSourceSystems: [],
      sentimentCounts: { positive: 0, neutral: 0, negative: 0 },
    },
    reviews,
    pagination: { page: 1, perPage: 9, totalPages: 1, totalCount: reviews.length },
  };
};
